import { WellStatusTimeScale } from "constants/charts.enums";
import { removeCount } from "utils/removeCountFromTitle";

import { tooltipRow } from "./rigFracTooltip";

// ECharts hands tooltip formatters an untyped params bag.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type TooltipParams = any;

export const WELL_STATUS_BUCKET_LABELS: Record<string, string> = {
  [WellStatusTimeScale.Yearly]: "Year",
  [WellStatusTimeScale.Monthly]: "Month",
  [WellStatusTimeScale.Weekly]: "Week",
  [WellStatusTimeScale.Daily]: "Day",
  [WellStatusTimeScale.Custom]: "Date"
};

interface WellStatusTooltipConfig {
  /** Omitted (or empty) when there is no real group-by title to name the row. */
  groupByLabel?: Maybe<string>;
  /** Group name comes from the series (over time) or the category axis (current). */
  groupFrom: "series" | "category";
  /** Fixed metric label; omitted when the hovered series IS the status. */
  metricLabel?: Maybe<string>;
  /** Stack total per dataIndex; omit for single-value bars (no "of"). */
  totals?: Maybe<readonly number[]>;
  bucketLabel?: Maybe<string>;
}

/**
 * Item-trigger tooltip matching Insights: "<group by>: <group>",
 * "<status>: <value> (of <total>)" and, over time, "<bucket>: <date>". Totals are
 * precomputed from the full series because params omit legend-hidden ones.
 */
export function buildWellStatusItemTooltipFormatter({
  groupByLabel,
  groupFrom,
  metricLabel,
  totals,
  bucketLabel
}: WellStatusTooltipConfig): (params: TooltipParams) => string {
  return (params: TooltipParams) => {
    const p = Array.isArray(params) ? params[0] : params;
    if (!p) return "";
    const raw = Array.isArray(p.value) ? p.value[1] : p.value;
    const seriesName = String(p.seriesName ?? "");
    const category = String(p.name ?? p.axisValueLabel ?? "");
    let value = Math.round(Number(raw) || 0).toLocaleString();
    if (totals) value += ` (of ${(totals[p.dataIndex] ?? 0).toLocaleString()})`;
    // Over-time series names carry a well-count affix, e.g. "(343) Montney". Current
    // categories arrive already stripped; stripping again would eat a real "(05767)".
    return (
      (groupByLabel
        ? tooltipRow(
            groupByLabel,
            groupFrom === "series" ? removeCount(seriesName) : category
          )
        : "") +
      tooltipRow(metricLabel || removeCount(seriesName), value) +
      (bucketLabel ? tooltipRow(bucketLabel, category) : "")
    );
  };
}

// One pass, two views of each bucket: `raw` sizes the axis, `rounded` is the sum
// of the per-segment values the tooltip rows display, so "(of N)" always adds up.
export const stackBucketTotals = (
  series: readonly { y?: Maybe<ArrayLike<unknown>> }[],
  length: number
): { raw: number[]; rounded: number[] } => {
  const raw: number[] = [];
  const rounded: number[] = [];
  for (let i = 0; i < length; i++) {
    let rawSum = 0;
    let roundedSum = 0;
    for (const s of series) {
      const v = Number(s.y?.[i]) || 0;
      rawSum += v;
      roundedSum += Math.round(v);
    }
    raw.push(rawSum);
    rounded.push(roundedSum);
  }
  return { raw, rounded };
};
